import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtMoney, FORMAT_LABELS } from "@/lib/fmt";
import { Plus, Pencil, Trash2, X } from "lucide-react";

export default function AdminTitles() {
  const [titles, setTitles] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const [t, a] = await Promise.all([api.get("/admin/titles"), api.get("/admin/authors")]);
    setTitles(t.data); setAuthors(a.data);
  };
  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    setErr("");
    try {
      if (editing) await api.patch(`/admin/titles/${editing.id}`, form);
      else await api.post("/admin/titles", form);
      setEditing(null); setShowNew(false); load();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this title?")) return;
    await api.delete(`/admin/titles/${id}`); load();
  };

  return (
    <div className="space-y-10" data-testid="admin-titles-page">
      <header className="flex items-center justify-between">
        <div>
          <p className="hh-overline mb-3">Admin · Titles</p>
          <h1 className="font-serif text-4xl text-hh-ink tracking-tight">Title catalogue</h1>
        </div>
        <button onClick={() => { setShowNew(true); setEditing(null); }} className="hh-btn-primary" data-testid="new-title-btn">
          <Plus size={16} strokeWidth={1.8} className="mr-2" /> New title
        </button>
      </header>

      <div className="hh-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-hh-muted/60 border-b border-hh-border">
            <tr className="text-left">
              <th className="px-6 py-4 hh-overline">Title</th>
              <th className="px-6 py-4 hh-overline">Author</th>
              <th className="px-6 py-4 hh-overline">ISBN</th>
              <th className="px-6 py-4 hh-overline">Format</th>
              <th className="px-6 py-4 hh-overline">RRP</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {titles.map((t) => (
              <tr key={t.id} className="border-b border-hh-border last:border-0" data-testid={`title-row-${t.isbn}`}>
                <td className="px-6 py-4 font-serif text-hh-ink">{t.title}</td>
                <td className="px-6 py-4 text-hh-ink2">{t.author_name}</td>
                <td className="px-6 py-4 text-hh-ink font-mono text-xs">{t.isbn}</td>
                <td className="px-6 py-4 text-hh-ink2">{FORMAT_LABELS[t.format] || t.format}</td>
                <td className="px-6 py-4 text-hh-ink2">{t.list_price ? fmtMoney(t.list_price, t.currency || "AUD") : "—"}</td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(t)} className="p-2 rounded-full hover:bg-hh-muted text-hh-ink2" data-testid={`edit-title-${t.isbn}`}>
                    <Pencil size={14} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => onDelete(t.id)} className="p-2 rounded-full hover:bg-hh-muted text-hh-danger ml-1" data-testid={`delete-title-${t.isbn}`}>
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
            {titles.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-hh-ink2">No titles yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(editing || showNew) && (
        <TitleForm
          initial={editing}
          authors={authors}
          onCancel={() => { setEditing(null); setShowNew(false); setErr(""); }}
          onSave={onSave}
          err={err}
        />
      )}
    </div>
  );
}

function TitleForm({ initial, authors, onCancel, onSave, err }) {
  const [isbn, setIsbn] = useState(initial?.isbn || "");
  const [title, setTitle] = useState(initial?.title || "");
  const [authorId, setAuthorId] = useState(initial?.author_id || authors[0]?.id || "");
  const [format, setFormat] = useState(initial?.format || "paperback");
  const [listPrice, setListPrice] = useState(initial?.list_price ?? "");
  const [currency, setCurrency] = useState(initial?.currency || "AUD");
  const [pubDate, setPubDate] = useState(initial?.publication_date || "");
  const [coverUrl, setCoverUrl] = useState(initial?.cover_url || "");
  const [directRoyalty, setDirectRoyalty] = useState(initial?.direct_royalty_per_book ?? "");
  const [directCurrency, setDirectCurrency] = useState(initial?.direct_currency || "AUD");

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-8 shadow-2xl" data-testid="title-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl text-hh-ink">{initial ? "Edit title" : "Add title"}</h2>
          <button onClick={onCancel}><X size={18} className="text-hh-ink2" /></button>
        </div>
        <div className="space-y-4">
          <Field label="ISBN" value={isbn} onChange={setIsbn} testid="title-isbn" />
          <Field label="Title" value={title} onChange={setTitle} testid="title-title" />
          <div>
            <label className="block text-sm text-hh-ink2 mb-2">Author</label>
            <select value={authorId} onChange={(e) => setAuthorId(e.target.value)} data-testid="title-author"
              className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink">
              {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-hh-ink2 mb-2">Format</label>
              <select value={format} onChange={(e) => setFormat(e.target.value)} data-testid="title-format"
                className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink">
                <option value="paperback">Paperback</option>
                <option value="hardcover">Hardcover</option>
                <option value="ebook">eBook</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-hh-ink2 mb-2">Currency</label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="title-currency"
                className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink">
                <option>AUD</option><option>USD</option><option>GBP</option><option>EUR</option>
              </select>
            </div>
          </div>
          <Field label="RRP" type="number" value={listPrice} onChange={(v) => setListPrice(parseFloat(v) || 0)} testid="title-price" optional />
          <Field label="Publication date (YYYY-MM-DD)" value={pubDate} onChange={setPubDate} testid="title-pubdate" optional />
          <Field label="Cover image URL" value={coverUrl} onChange={setCoverUrl} testid="title-cover" optional />
          <div className="border-t border-hh-border pt-4 mt-2">
            <p className="hh-overline mb-3">WooCommerce / direct-sale royalty</p>
            <p className="text-xs text-hh-ink2 mb-3">
              When a WordPress (Healing House) sale comes in for this title, the author receives this royalty per book.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Royalty per book" type="number" value={directRoyalty}
                onChange={(v) => setDirectRoyalty(v)} testid="title-direct-royalty" optional />
              <div>
                <label className="block text-sm text-hh-ink2 mb-2">Direct currency</label>
                <select value={directCurrency} onChange={(e) => setDirectCurrency(e.target.value)} data-testid="title-direct-currency"
                  className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink">
                  <option>AUD</option><option>USD</option><option>GBP</option><option>EUR</option>
                </select>
              </div>
            </div>
          </div>
          {err && <div className="text-sm text-hh-danger">{err}</div>}
        </div>
        <div className="mt-8 flex gap-3 justify-end">
          <button onClick={onCancel} className="hh-btn-ghost">Cancel</button>
          <button onClick={() => onSave({
              isbn, title, author_id: authorId, format,
              list_price: listPrice ? parseFloat(listPrice) : null,
              currency, publication_date: pubDate || null, cover_url: coverUrl || null,
              retail_links: {},
              direct_royalty_per_book: directRoyalty !== "" ? parseFloat(directRoyalty) : null,
              direct_currency: directCurrency,
            })} className="hh-btn-primary" data-testid="title-save">
            {initial ? "Save changes" : "Create title"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, optional }) {
  return (
    <div>
      <label className="block text-sm text-hh-ink2 mb-2">
        {label} {optional && <span className="text-xs">(optional)</span>}
      </label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink focus:outline-none focus:ring-2 focus:ring-hh-primary/40" />
    </div>
  );
}
